/**
 * Admin Settings Page
 *
 * Platform-level settings and configuration for administrators.
 * For org admins, shows organisation-specific settings including case defaults.
 */

import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks'
import { OQTemplateManager } from '../components/settings/oq-template-manager'
import { SettingCard } from './admin-settings/SettingCard'
import { AdminSettingsHeader } from './admin-settings/AdminSettingsHeader'
import { CaseDefaultsSection } from './admin-settings/CaseDefaultsSection'
import { AuthenticationSection } from './admin-settings/AuthenticationSection'
import { SecuritySection } from './admin-settings/SecuritySection'
import { SystemSection } from './admin-settings/SystemSection'
import { DangerZoneSection } from './admin-settings/DangerZoneSection'
import { useAdminSettingsForm } from './admin-settings/use-admin-settings-form'
import { useCaseDefaultsForm } from './admin-settings/use-case-defaults-form'

function CurrentAdminInfo(): React.ReactElement {
  const { t } = useTranslation('admin')
  const { user } = useAuth()
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-card)] p-4">
      <p className="text-sm text-[var(--text-dim)]">
        {t('adminSettings.signedInAs')}{' '}
        <span className="font-medium text-[var(--text)]">
          {user?.displayName ?? user?.email}
        </span>{' '}
        ({user?.role})
      </p>
    </div>
  )
}

export function AdminSettingsPage(): React.ReactElement {
  const { t } = useTranslation('admin')
  const { user } = useAuth()
  const isOrgAdmin = user?.role === 'org_admin'

  const adminForm = useAdminSettingsForm()
  const caseDefaults = useCaseDefaultsForm()

  return (
    <div className="space-y-6">
      <AdminSettingsHeader
        saving={adminForm.saving}
        savedMessage={adminForm.savedMessage}
        onSave={() => {
          void adminForm.handleSave()
        }}
      />
      <CurrentAdminInfo />

      {isOrgAdmin && (
        <CaseDefaultsSection
          {...caseDefaults}
          onSave={() => {
            void caseDefaults.onSave()
          }}
        />
      )}

      {isOrgAdmin && (
        <SettingCard
          title={t('adminSettings.oqTemplates')}
          description={t('adminSettings.oqTemplatesDesc')}
        >
          <OQTemplateManager />
        </SettingCard>
      )}

      <AuthenticationSection
        isOrgAdmin={isOrgAdmin}
        settings={adminForm.settings}
        setSettings={adminForm.setSettings}
      />

      <SecuritySection
        isOrgAdmin={isOrgAdmin}
        settings={adminForm.settings}
        setSettings={adminForm.setSettings}
      />

      {!isOrgAdmin && (
        <SystemSection settings={adminForm.settings} setSettings={adminForm.setSettings} />
      )}

      {!isOrgAdmin && <DangerZoneSection />}
    </div>
  )
}
